import React from 'react';
import { Spin, Grid } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useOvrChat } from './useOvrChat';
import ChatHeader from './ChatHeader';
import MessageInput from './MessageInput';
import ConversationList from './ConversationsLists';
import CreateOvrModal from './CreateOvrModal';
import ChatMessages from './ChatMessages';
import EmptyState from './EmptyState';

dayjs.extend(relativeTime);

const { useBreakpoint } = Grid;

const OvrChatScreen = () => {
    const screens = useBreakpoint();
    const isMobile = screens.md === false; // < 768px

    const {
        // State
        isCreateModalVisible,
        setIsCreateModalVisible,
        selectedOvr,
        setSelectedOvr,
        messageInput,
        setMessageInput,
        createForm,
        role,

        // Data
        ovrs,
        staffData,

        // Loading states
        ovrsLoading,
        staffLoading,
        specificOvrLoading,
        isCreating,
        isSending,
        isClosing,
        isMoving,

        // Handlers
        handleCreateOvr,
        handleSendMessage,
        handleCloseOvr,
        handleMoveOvr,

        // Helpers
        getStatusBadge,
        getStaffInfo,
        isMessageFromCurrentUser,
    } = useOvrChat();

    if (ovrsLoading || staffLoading || specificOvrLoading) {
        return (
            <div className="flex h-screen items-center justify-center bg-gray-50">
                <Spin size="large" tip="Loading OVR conversations..." />
            </div>
        );
    }

    // Mobile = master/detail: show the list OR the open conversation (with a back button).
    // Desktop = side by side.
    const showChat = !isMobile || !!selectedOvr;
    const showList = !isMobile || !selectedOvr;

    return (
        <div className="flex w-full bg-gray-100 overflow-x-hidden">
            {/* Main Chat Area */}
            {showChat && (
                <div className="w-full md:w-[70%] h-[100dvh] md:h-screen flex flex-col bg-white min-w-0">
                    {selectedOvr ? (
                        <>
                            <div className="shrink-0">
                                <ChatHeader
                                    ovr={selectedOvr}
                                    getStaffInfo={getStaffInfo}
                                    getStatusBadge={getStatusBadge}
                                    onCloseOvr={handleCloseOvr}
                                    onMoveOvr={handleMoveOvr}
                                    isClosing={isClosing}
                                    isMoving={isMoving}
                                    role={role}
                                    staffData={staffData}
                                    staffLoading={staffLoading}
                                    onBack={isMobile ? () => setSelectedOvr(null) : undefined}
                                />
                            </div>

                            <ChatMessages
                                ovr={selectedOvr}
                                getStaffInfo={getStaffInfo}
                                isMessageFromCurrentUser={isMessageFromCurrentUser}
                            />

                            <MessageInput
                                messageInput={messageInput}
                                setMessageInput={setMessageInput}
                                onSendMessage={handleSendMessage}
                                isSending={isSending}
                                isClosed={selectedOvr.status === 2}
                            />
                        </>
                    ) : (
                        <EmptyState onCreateNew={() => setIsCreateModalVisible(true)} />
                    )}
                </div>
            )}

            {/* Sidebar - Conversation List */}
            {showList && (
                <ConversationList
                    ovrs={ovrs}
                    selectedOvr={selectedOvr}
                    onSelectOvr={setSelectedOvr}
                    onCreateNew={() => setIsCreateModalVisible(true)}
                    getStatusBadge={getStatusBadge}
                />
            )}

            {/* Create OVR Modal */}
            <CreateOvrModal
                visible={isCreateModalVisible}
                onCancel={() => {
                    setIsCreateModalVisible(false);
                    createForm.resetFields();
                }}
                onSubmit={handleCreateOvr}
                form={createForm}
                staffData={staffData}
                isLoading={isCreating}
                staffLoading={staffLoading}
            />
        </div>
    );
};

export default OvrChatScreen;